import React, { useMemo, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { logoImg } from '../../utils/images';
import '../../components/LoginSupabase/LoginS.scss';
import './EmailOtpPage.scss';

type EmailOtpFlow = 'signup' | 'recovery';

export const EmailOtpPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const email = searchParams.get('email') ?? '';
  const flow = (searchParams.get('flow') === 'recovery' ? 'recovery' : 'signup') as EmailOtpFlow;
  const isRecoveryFlow = flow === 'recovery';

  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const title = isRecoveryFlow ? 'Verify Reset Code' : 'Verify Email';
  const description = useMemo(() => {
    if (!email) {
      return 'Enter the one-time code sent to your email.';
    }

    return isRecoveryFlow
      ? `Enter the code sent to ${email} to continue resetting your password.`
      : `Enter the code sent to ${email} to verify your new account.`;
  }, [email, isRecoveryFlow]);

  const handleVerifyOtp = async () => {
    setError(null);
    setSuccess(null);

    if (!email) {
      setError('Missing email address. Please start again.');
      return;
    }

    if (!otp.trim()) {
      setError('Please enter the verification code.');
      return;
    }

    if (isRecoveryFlow && newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (isRecoveryFlow && newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp.trim(),
        type: isRecoveryFlow ? 'recovery' : 'signup',
      });

      if (error) throw error;

      if (isRecoveryFlow) {
        const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
        if (updateError) {
          await supabase.auth.signOut({ scope: 'local' });
          throw updateError;
        }

        await supabase.auth.signOut({ scope: 'local' });
        navigate('/', { replace: true });
      } else {
        await supabase.auth.signOut({ scope: 'local' });
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      if (isRecoveryFlow) {
        await supabase.auth.signOut({ scope: 'local' });
      }
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError(null);
    setSuccess(null);

    if (!email) {
      setError('Missing email address. Please start again.');
      return;
    }

    setIsLoading(true);
    try {
      if (isRecoveryFlow) {
        await supabase.auth.signOut({ scope: 'local' });
      }

      const { error } = isRecoveryFlow
        ? await supabase.auth.resetPasswordForEmail(email)
        : await supabase.auth.resend({ email, type: 'signup' });

      if (error) throw error;
      setSuccess('A new verification code has been sent.');
    } catch (err: any) {
      setError(err.message || 'Unable to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="emailOtpPage">
      <div className="login">
        <div className="login__container">
          <div className="login__t">
            <div className="login__logo-wrapper">
              <img src={logoImg} alt="Dovery" className="login__logo" />
            </div>

            <h1 className="email-otp__title">{title}</h1>
            <p className="auth-form__description">{description}</p>

            {success && <p className="auth-message__text">{success}</p>}
            {error && <p className="login-form__error">{error}</p>}

            <div className="login-form">
                <div className="login-form__field">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="login-form__input"
                    placeholder="Enter code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    autoComplete="one-time-code"
                  />
                </div>

                {isRecoveryFlow && (
                  <>
                    <div className="login-form__field">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="login-form__input login-form__input--with-toggle"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="login-form__password-toggle"
                        onClick={() => setShowPassword((current) => !current)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>

                    <div className="login-form__field">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="login-form__input login-form__input--with-toggle"
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="login-form__password-toggle"
                        onClick={() => setShowConfirmPassword((current) => !current)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        title={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </>
                )}

                <button
                  type="button"
                  className="login-form__submit"
                  onClick={handleVerifyOtp}
                  disabled={isLoading}
                >
                  {isLoading
                    ? 'Please wait...'
                    : isRecoveryFlow
                      ? 'Reset Password'
                      : 'Verify Code'}
                </button>

                <div className="email-otp__actions">
                  <button
                    type="button"
                    className="email-otp__link-button"
                    onClick={handleResendOtp}
                    disabled={isLoading}
                  >
                    Resend Code
                  </button>
                  <button
                    type="button"
                    className="email-otp__link-button"
                    onClick={() => navigate(isRecoveryFlow ? '/forgot-password' : '/sign-up')}
                    disabled={isLoading}
                  >
                    Back
                  </button>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
